import { GeoCore } from '@/api/config/geocore';
import { GeoPackageReader } from '@/api/config/reader/geopackage-reader';
import { ShapefileReader } from '@/api/config/reader/shapefile-reader';
import { AbstractBaseLayerEntryConfig } from '@/api/config/validation-classes/abstract-base-layer-entry-config';
import { ConfigBaseClass } from '@/api/config/validation-classes/config-base-class';
import EventHelper, { type EventDelegateBase } from '@/api/events/event-helper';
import {
  mapConfigLayerEntryIsGeoCore,
  mapConfigLayerEntryIsGeoPackage,
  mapConfigLayerEntryIsRCS,
  mapConfigLayerEntryIsShapefile,
  type GeoCoreLayerConfig,
  type MapConfigLayerEntry,
  type TypeLayerEntryConfig,
  type TypeGeoviewLayerConfig,
} from '@/api/types/layer-schema-types';
import type { TypeDisplayLanguage } from '@/api/types/map-schema-types';
import { ConfigValidation } from '@/api/config/config-validation';
import type { GeoViewGeoChartConfig, GeoViewTimeSliderConfig } from '@/api/config/reader/uuid-config-reader';
import { GroupLayerEntryConfig } from '@/api/config/validation-classes/group-layer-entry-config';
import { EsriDynamicLayerEntryConfig } from '@/api/config/validation-classes/raster-validation-classes/esri-dynamic-layer-entry-config';
import { CsvLayerEntryConfig } from '@/api/config/validation-classes/vector-validation-classes/csv-layer-entry-config';
import { EsriFeatureLayerEntryConfig } from '@/api/config/validation-classes/vector-validation-classes/esri-feature-layer-entry-config';
import { EsriImageLayerEntryConfig } from '@/api/config/validation-classes/raster-validation-classes/esri-image-layer-entry-config';
import { GeoJSONLayerEntryConfig } from '@/api/config/validation-classes/vector-validation-classes/geojson-layer-entry-config';
import { GeoTIFFLayerEntryConfig } from '@/api/config/validation-classes/raster-validation-classes/geotiff-layer-entry-config';
import { ImageStaticLayerEntryConfig } from '@/api/config/validation-classes/raster-validation-classes/image-static-layer-entry-config';
import { KmlLayerEntryConfig } from '@/api/config/validation-classes/vector-validation-classes/kml-layer-entry-config';
import { OgcFeatureLayerEntryConfig } from '@/api/config/validation-classes/vector-validation-classes/ogc-layer-entry-config';
import { OgcWfsLayerEntryConfig } from '@/api/config/validation-classes/vector-validation-classes/wfs-layer-entry-config';
import { WkbLayerEntryConfig } from '@/api/config/validation-classes/vector-validation-classes/wkb-layer-entry-config';
import { OgcWmsLayerEntryConfig } from '@/api/config/validation-classes/raster-validation-classes/ogc-wms-layer-entry-config';
import { OgcWmtsLayerEntryConfig } from '@/api/config/validation-classes/raster-validation-classes/ogc-wmts-layer-entry-config';
import { XYZTilesLayerEntryConfig } from '@/api/config/validation-classes/raster-validation-classes/xyz-layer-entry-config';
import { VectorTilesLayerEntryConfig } from '@/api/config/validation-classes/raster-validation-classes/vector-tiles-layer-entry-config';
import { AbstractMapViewerController } from '@/core/controllers/base/abstract-map-viewer-controller';
import type { ControllerRegistry } from '@/core/controllers/base/controller-registry';
import type { LayerDomain } from '@/core/domains/layer-domain';
import type { UIDomain } from '@/core/domains/ui-domain';
import { generateId, isValidUUID } from '@/core/utils/utilities';
import { formatError, NotSupportedError, PromiseRejectErrorWrapper } from '@/core/exceptions/core-exceptions';
import { GeoViewError, LayerEntryConfigLayerIdMissingError, LayerFailedToLoadError } from '@/core/exceptions/geoview-exceptions';
import { LayerEntryConfigError } from '@/core/exceptions/layer-entry-config-exceptions';
import { LayerCreatedTwiceError } from '@/core/exceptions/layer-exceptions';
import {
  getStoreLayerLegendLayers,
  getStoreLayerSelectedLayerPath,
  setStoreLayerSelectedLayersTabLayer,
  getStoreLayerOrderedLayerPaths,
  addStoreLayerInitialFilter,
} from '@/core/stores/states/layer-state';
import { isStoreGeochartInitialized } from '@/core/stores/states/geochart-state';
import { isStoreSwiperInitialized } from '@/core/stores/states/swiper-state';
import { isStoreTimeSliderInitialized, removeStoreTimeSliderLayer } from '@/core/stores/states/time-slider-state';
import { logger } from '@/core/utils/logger';
import type {
  AbstractGeoViewLayer,
  LayerEntryRegisterInitEvent,
  LayerGroupCreatedEvent,
  LayerGVCreatedEvent,
} from '@/geo/layer/geoview-layers/abstract-geoview-layers';
import type { AbstractGVLayer, LayerMessageEvent } from '@/geo/layer/gv-layers/abstract-gv-layer';
import type { MapViewer } from '@/geo/map/map-viewer';
import { AbstractGVVector } from '@/geo/layer/gv-layers/vector/abstract-gv-vector';
import { GeoJSON } from '@/geo/layer/geoview-layers/vector/geojson';
import { WMS } from '@/geo/layer/geoview-layers/raster/wms';
import { WMTS } from '@/geo/layer/geoview-layers/raster/wmts';
import { EsriDynamic } from '@/geo/layer/geoview-layers/raster/esri-dynamic';
import { EsriFeature } from '@/geo/layer/geoview-layers/vector/esri-feature';
import { EsriImage } from '@/geo/layer/geoview-layers/raster/esri-image';
import { GeoTIFF } from '@/geo/layer/geoview-layers/raster/geotiff';
import { ImageStatic } from '@/geo/layer/geoview-layers/raster/image-static';
import { KML } from '@/geo/layer/geoview-layers/vector/kml';
import { WFS } from '@/geo/layer/geoview-layers/vector/wfs';
import { OgcFeature } from '@/geo/layer/geoview-layers/vector/ogc-feature';
import { XYZTiles } from '@/geo/layer/geoview-layers/raster/xyz-tiles';
import { VectorTiles } from '@/geo/layer/geoview-layers/raster/vector-tiles';
import { CSV } from '@/geo/layer/geoview-layers/vector/csv';
import { WKB } from '@/geo/layer/geoview-layers/vector/wkb';
import { LayerGeoCoreError } from '@/core/exceptions/geocore-exceptions';
import { GVGroupLayer } from '@/geo/layer/gv-layers/gv-group-layer';

export class LayerCreatorController extends AbstractMapViewerController {
  /** Reference on the UI domain. */
  #uiDomain: UIDomain;

  /** Reference on the layer domain. */
  #layerDomain: LayerDomain;

  /** Dictionary holding all the geoview layers used for processing layer entry configs */
  #geoviewLayers: { [geoviewLayerId: string]: AbstractGeoViewLayer } = {};

  /** Original GeoView layer configs used to rebuild layers after a failed load. */
  #geoviewLayerConfigSnapshots: { [geoviewLayerId: string]: TypeGeoviewLayerConfig } = {};

  /** Callback delegates for the layer config added event */
  #onLayerConfigAddedHandlers: LayerBuilderDelegate[] = [];

  /** Callback delegates for the layer config error event */
  #onLayerConfigErrorHandlers: LayerConfigErrorDelegate[] = [];

  /** Callback delegates for the layer config removed event */
  #onLayerConfigRemovedHandlers: LayerPathDelegate[] = [];

  /** Callback delegates for the layer created event */
  #onLayerCreatedHandlers: LayerDelegate[] = [];

  /**
   * Creates an instance of the LayerCreator class.
   *
   * @param mapViewer - The map viewer instance to associate with this controller
   * @param controllerRegistry - The controller registry for accessing sibling controllers
   * @param layerDomain - The layer domain to be used by the LayerCreator
   */
  constructor(mapViewer: MapViewer, controllerRegistry: ControllerRegistry, uiDomain: UIDomain, layerDomain: LayerDomain) {
    super(mapViewer, controllerRegistry);
    this.#uiDomain = uiDomain;
    this.#layerDomain = layerDomain;
  }

  /**
   * Loads layers that were passed in with the map config.
   *
   * @param mapConfigLayerEntries - An optional array containing layers passed within the map config
   * @returns A promise that resolves when everything is done
   */
  async loadListOfGeoviewLayer(mapConfigLayerEntries: MapConfigLayerEntry[]): Promise<void> {
    const validGeoviewLayerConfigs = this.#deleteDuplicateAndMultipleUuidGeoviewLayerConfig(mapConfigLayerEntries);

    // Make sure to convert all valid map config layer entry into a GeoviewLayerConfig (use filtered list to exclude duplicates)
    const promisesOfGeoviewLayers = LayerCreatorController.convertMapConfigsToGeoviewLayerConfig(
      this.getMapId(),
      this.getControllersRegistry().layerController.getGeoviewLayerIds(),
      this.getMapViewer().getDisplayLanguage(),
      validGeoviewLayerConfigs,
      (layerPath: string, geochartConfig: GeoViewGeoChartConfig) => {
        // Add the chart to the store
        this.getControllersRegistry().geoChartController?.addChart(layerPath, geochartConfig);

        // Make sure geochart tab is shown
        this.getControllersRegistry().uiController.showTabButton('geochart');

        // Log
        logger.logInfo('Added GeoChart configs for layer path:', layerPath);
      },
      (timeSliderConfigs: GeoViewTimeSliderConfig[]) => {
        // Merge time-slider configs into corePackagesConfig (stored for the plugin to pick up if it's configured)
        this.mergeTimeSliderConfigsIntoCorePackages(timeSliderConfigs);

        // Log
        logger.logInfo('Added Time Slider configs from GeoCore');
      },
      (mapConfigLayerEntry: MapConfigLayerEntry, error: unknown) => {
        // Show the error(s)
        this.showLayerError(error, mapConfigLayerEntry.geoviewLayerId, mapConfigLayerEntry.geoviewLayerName);
      }
    );

    // Wait for all promises (GeoCore ones) to process
    // The reason for the Promise.allSettled is because of synch issues with the 'setMapOrderedLayers' which happens below and the
    // other setMapOrderedLayers that happen in parallel via the ADD_LAYER events ping/pong'ing, making the setMapOrdered below fail
    // if we don't stage the promises. If we don't stage the promises, sometimes I have 4 layers loaded in 'Details' and sometimes
    // I have 3 layers loaded in Details - for example.
    // To fix this, we'll have to synch the ADD_LAYER events and make sure those 'know' what order they should be in when they
    // propagate the mapOrderedLayers in their processes. For now at least, this is repeating the same behavior until the events are fixed.
    const orderedLayers: string[] = getStoreLayerOrderedLayerPaths(this.getMapId()).length
      ? [...getStoreLayerOrderedLayerPaths(this.getMapId())]
      : [];
    const promisedLayers = await Promise.allSettled(promisesOfGeoviewLayers);

    // For each layers in the fulfilled promises only
    promisedLayers.forEach((promise) => {
      // If fullfilled
      if (promise.status === 'fulfilled') {
        // Get the geoview layer config
        const geoviewLayerConfig = promise.value;

        try {
          // Validate that the layer entry has at least 1 listOfLayerEntry with a layerId
          if (
            !geoviewLayerConfig.listOfLayerEntryConfig?.length ||
            geoviewLayerConfig.listOfLayerEntryConfig.find((layerEntry) => layerEntry.layerId === undefined)
          ) {
            // Throw error for that geoview layer config
            throw new LayerEntryConfigLayerIdMissingError(geoviewLayerConfig.geoviewLayerName ?? geoviewLayerConfig.geoviewLayerId);
          }

          // Generate array of layer paths for non-basemap layers
          if (geoviewLayerConfig.useAsBasemap !== true) {
            const layerPaths = AbstractMapViewerController.generateOrderedLayerPaths(geoviewLayerConfig);
            orderedLayers.push(...layerPaths);

            // If a configured layers-panel selection references only this layer's root id (e.g. a GeoCore UUID whose
            // sublayer path is unknown at config time), rewrite it to the first resolved layer path under that root id.
            if (layerPaths.length) this.#resolveConfiguredSelectedLayersLayerPath(geoviewLayerConfig.geoviewLayerId, layerPaths[0]);
          }

          // Add it
          this.addGeoviewLayer(geoviewLayerConfig);
        } catch (error: unknown) {
          // An error happening here likely means a particular, trivial, config error.
          // The majority of typicaly errors happen in the addGeoviewLayer promise catcher, not here.

          // Remove the layer paths that were added to orderedLayers before the error
          if (geoviewLayerConfig.useAsBasemap !== true) {
            const failedPaths = AbstractMapViewerController.generateOrderedLayerPaths(geoviewLayerConfig);
            failedPaths.forEach((failedPath) => {
              const idx = orderedLayers.indexOf(failedPath);
              if (idx !== -1) orderedLayers.splice(idx, 1);
            });
          }

          // Show the error(s)
          this.showLayerError(error, geoviewLayerConfig.geoviewLayerId, geoviewLayerConfig.geoviewLayerName);
        }
      } else {
        // Depending on the error
        let uuids;
        if (promise.reason instanceof LayerGeoCoreError) {
          ({ uuids } = promise.reason);
        }

        // For each uuid that failed
        uuids?.forEach((uuid: string) => {
          // Get the index at which the TypeGeoviewLayerConfig happened
          const index = validGeoviewLayerConfigs.findIndex((mapLayerEntry) => mapLayerEntry.geoviewLayerId === uuid);

          // If found
          if (index >= 0) {
            // Remove the entry
            validGeoviewLayerConfigs.splice(index, 1);
          }
        });
      }
    });

    // At this point, we've removed the duplicated geocore (DuplicateAndMultipleUuidGeoviewLayerConfig) and the
    // geocore that were failing were removed from the validGeoviewLayerConfigs variable.
    // Time to update the list we received in param so that the rest of the application works with that list.
    // This is notably so that the map loads even if no geocore layers were valid

    // Replace the array received in param
    mapConfigLayerEntries.splice(0, mapConfigLayerEntries.length, ...validGeoviewLayerConfigs);

    // Init ordered layers
    this.getControllersRegistry().layerController.setMapOrderedLayersDirectly(orderedLayers);
  }

  /**
   * Adds a Geoview Layer by GeoCore UUID.
   *
   * @param uuid - The GeoCore UUID to add to the map
   * @param layerEntryConfig - The optional layer configuration
   * @returns A promise that resolves with the added layer result or undefined when an error occurs
   */
  async addGeoviewLayerByGeoCoreUUID(uuid: string, layerEntryConfig?: string): Promise<GeoViewLayerAddedResult | undefined> {
    // Get the current geoview layer ids
    const currentGeoviewLayerIds = this.getControllersRegistry().layerController.getGeoviewLayerIds();

    if (currentGeoviewLayerIds.includes(uuid)) {
      // eslint-disable-next-line no-param-reassign
      uuid = `${uuid}:${generateId(8)}`;
    }

    try {
      // GV: This is here as a placeholder so that the layers will appear in the proper order,
      // GV: regardless of how quickly we get the response. It is removed, in the catch below, if the layer fails.
      this.getControllersRegistry().layerController.addOrderedLayerPath(uuid);

      const parsedLayerEntryConfig = layerEntryConfig ? JSON.parse(layerEntryConfig) : undefined;
      if (parsedLayerEntryConfig && !parsedLayerEntryConfig[0].layerId) parsedLayerEntryConfig[0].layerId = 'base-group';

      let optionalConfig: GeoCoreLayerConfig | undefined =
        parsedLayerEntryConfig && (parsedLayerEntryConfig[0].listOfLayerEntryConfig || parsedLayerEntryConfig[0].initialSettings)
          ? {
              geoviewLayerType: 'geoCore',
              geoviewLayerId: uuid,
              geoviewLayerName: parsedLayerEntryConfig[0].geoviewLayerName,
              listOfLayerEntryConfig: parsedLayerEntryConfig[0].geoviewLayerName
                ? parsedLayerEntryConfig[0].listOfLayerEntryConfig
                : parsedLayerEntryConfig,
              initialSettings: parsedLayerEntryConfig[0].initialSettings,
            }
          : undefined;

      // If a simplified config is provided, build a config with the layerName provided
      if (!optionalConfig && parsedLayerEntryConfig && (parsedLayerEntryConfig[0].layerName || parsedLayerEntryConfig[0].geoviewLayerName))
        optionalConfig = {
          geoviewLayerType: 'geoCore',
          geoviewLayerId: uuid,
          geoviewLayerName: parsedLayerEntryConfig[0].geoviewLayerName || parsedLayerEntryConfig[0].layerName,
        };

      // Create the layers from the UUID
      const response = await GeoCore.createLayerConfigFromUUID(
        uuid,
        currentGeoviewLayerIds,
        this.getMapViewer().getDisplayLanguage(),
        this.getMapId(),
        optionalConfig
      );
      const geoviewLayerConfig = response.config;

      // If a Geochart is initialized
      if (isStoreGeochartInitialized(this.getMapId())) {
        // For each geocharts configuration
        Object.entries(response.geocharts).forEach(([layerPath, geochartConfig]) => {
          // Add a GeoChart configuration on-the-fly
          this.getControllersRegistry().geoChartController?.addChart(layerPath, geochartConfig);

          // Make sure geochart tab is shown
          this.getControllersRegistry().uiController.showTabButton('geochart');
        });
      }

      // If time-slider configs were returned from GeoCore, merge them into corePackagesConfig
      if (response.timeSliderConfigs.length > 0) {
        this.mergeTimeSliderConfigsIntoCorePackages(response.timeSliderConfigs);
      }

      if (geoviewLayerConfig.useAsBasemap === true) {
        // If a basemap, remove the ordered layer placeholder as basemap are not part of the ordered layers.
        this.getControllersRegistry().layerController.removeOrderedLayerPath(geoviewLayerConfig.geoviewLayerId, true);
      }

      // Add the geoview layer
      return this.addGeoviewLayer(geoviewLayerConfig);
    } catch (error: unknown) {
      // An error happening here likely means an issue with the UUID or a trivial config error.
      // The majority of typicaly errors happen in the addGeoviewLayer promise catcher, not here.

      // Remove geoCore ordered layer placeholder
      this.getControllersRegistry().layerController.removeOrderedLayerPath(uuid, false);

      // Show the error(s)
      this.showLayerError(error, uuid);
    }

    // None
    return undefined;
  }

  /**
   * Adds a layer to the map.
   *
   * This is the main method to add a GeoView Layer on the map. It handles all the processing, including the validations,
   * and makes sure to inform the layer sets about the layer. The result contains the instanciated GeoViewLayer along
   * with a promise that will resolve when the layer will be officially on the map.
   *
   * @param geoviewLayerConfig - The geoview layer configuration to add
   * @param abortSignal - Optional {@link AbortSignal} used to cancel the layer creation process
   * @returns The result of the addition of the geoview layer
   * @throws {LayerCreatedTwiceError} When there already is a layer on the map with the provided geoviewLayerId
   */
  addGeoviewLayer(geoviewLayerConfig: TypeGeoviewLayerConfig, abortSignal?: AbortSignal): GeoViewLayerAddedResult {
    const reloadConfigSnapshot = structuredClone({
      ...geoviewLayerConfig,
      listOfLayerEntryConfig: geoviewLayerConfig.listOfLayerEntryConfig.map((config) =>
        config instanceof ConfigBaseClass ? config.toJson<TypeLayerEntryConfig>() : config
      ),
    });

    // Check if the geoviewLayerConfig has its list of layer list entry already validated or not
    // GV It's already validated when it comes from:
    // GV - (1) validateLayersConfigAgainstSchema (upon map initialization load or in add-new-layer component)
    // GV - (2) GeoCore within 'createLayerConfigFromUUID'
    // GV It's not already validated when 'addGeoviewLayer' is called directly from external
    if (!ConfigValidation.isListOfLayerEntryConfigValidated(geoviewLayerConfig.listOfLayerEntryConfig)) {
      // Validate the layer entry configs at last minute
      ConfigValidation.validateListOfGeoviewLayerConfig([geoviewLayerConfig]);
    }

    // If the geoviewlayerid already exists, throw
    if (this.getControllersRegistry().layerController.getGeoviewLayerIds().includes(geoviewLayerConfig.geoviewLayerId)) {
      // Throw that the geoview layer id was already created
      throw new LayerCreatedTwiceError(geoviewLayerConfig.geoviewLayerId, geoviewLayerConfig.geoviewLayerName);
    }

    // Keep a snapshot of the GeoView layer config used to rebuild this layer after a failed load.
    this.#geoviewLayerConfigSnapshots[geoviewLayerConfig.geoviewLayerId] = reloadConfigSnapshot;

    // Process the addition of the layer
    const result: GeoViewLayerAddedResult = this.#addGeoviewLayerStep2(geoviewLayerConfig, abortSignal);

    // If any errors happened during the processing, we want to show them in the notifications
    result.promiseLayer.catch((error: unknown) => {
      // GV This is the major catcher of many possible layer processing issues

      // Show the error(s).
      this.showLayerError(error, geoviewLayerConfig.geoviewLayerId, geoviewLayerConfig.geoviewLayerName);
    });

    // Return the result
    return result;
  }

  /**
   * Refreshes all GeoCore layers by removing and re-adding them, then restoring the original layer order and visibility.
   *
   * Uses Promise.allSettled so that ordered layer paths include all children before restoring state.
   */
  reloadGeocoreLayers(): void {
    const { layerController } = this.getControllersRegistry();
    const originalOrderedLayers = [...getStoreLayerOrderedLayerPaths(this.getMapId())];
    const originalLegendLayersInfo = getStoreLayerLegendLayers(this.getMapId());

    // Collect root-level GeoCore configs, remove each layer, and start re-adding — capturing both paths and promises
    const parentPaths: string[] = [];
    const reloadPromises = layerController
      .getLayerEntryConfigs()
      .filter((config) => isValidUUID(config.getGeoviewLayerId()) && !config.getParentLayerConfig())
      .map((config) => {
        parentPaths.push(config.layerPath);
        this.removeLayerUsingPath(config.layerPath);
        return this.addGeoviewLayerByGeoCoreUUID(config.getGeoviewLayerId());
      });

    // Have to do the Promise allSettled so the new ordered layers have all the children layerPaths
    Promise.allSettled(reloadPromises)
      .then(() => {
        // After each GeoCore layer loads, remove any new child paths that weren't in the original order
        parentPaths.forEach((parentPath) => {
          void this.onceLayerConfigAdded().then(() => {
            // Only process group layers — single-layer GeoCore UUIDs have no children to prune
            const layerConfig = this.getControllersRegistry().layerController.getLayerEntryConfigIfExists(parentPath);
            if (layerConfig instanceof GroupLayerEntryConfig) {
              this.#getAllChildPaths(parentPath)
                .filter((childPath) => !originalOrderedLayers.includes(childPath))
                .forEach((childPath) => this.removeLayerUsingPath(childPath));
            }
          });
        });

        // Restore original layer order
        layerController.setMapOrderedLayersDirectly(originalOrderedLayers);

        // Restore original visibility for each layer when it first loads
        originalOrderedLayers.forEach((layerPath) => {
          void this.#layerDomain
            .onceLayerFirstLoaded((event) => event.layer.getLayerPath() === layerPath)
            .then((event) => {
              const { visible } = originalLegendLayersInfo.find((info) => info.layerPath === layerPath) ?? {};
              event.layer?.setVisible(visible ?? true);
            });
        });
      })
      .catch((error: unknown) => {
        // Log
        logger.logError(error);
      });
  }

  /**
   * Reloads the owning GeoView layer from its original config and restores its ordered position.
   *
   * @param layerPath - The path to the layer to reload
   * @returns A promise that resolves after the full layer reload finishes
   */
  async reloadLayer(layerPath: string): Promise<void> {
    // Prefer reloading only the errored child when its parent group and siblings are healthy.
    if (await this.#tryReloadSingleErroredChild(layerPath)) return;

    // Use the root layer ID to retrieve the configuration snapshot required to reload the layer.
    const rootLayerId = layerPath.split('/')[0];
    const configSnapshot = this.#geoviewLayerConfigSnapshots[rootLayerId];
    if (!configSnapshot) {
      this.getMapViewer().notifications.showError('layers.errorNonreloadableLayer');
      return;
    }

    const { layerController } = this.getControllersRegistry();
    const rootEntryPaths = layerController
      .getLayerEntryConfigs()
      .filter((config) => config.getGeoviewLayerId() === rootLayerId && !config.getParentLayerConfig())
      .map((config) => config.layerPath);
    const originalOrderedPaths = [...getStoreLayerOrderedLayerPaths(this.getMapId())];
    const originalRootPaths = originalOrderedPaths.filter((path) => path === rootLayerId || path.startsWith(`${rootLayerId}/`));
    const originalMapLayerConfigs = this.getMapViewer().mapFeaturesConfig.map.listOfGeoviewLayerConfig;
    const originalMapConfigIndex = originalMapLayerConfigs?.findIndex((config) => config.geoviewLayerId === rootLayerId) ?? -1;

    try {
      // Remove every top-level entry so groups and failed-before-registration layers are rebuilt together.
      rootEntryPaths.reverse().forEach((rootEntryPath) => this.removeLayerUsingPath(rootEntryPath));

      // Removal may update the map config; retain the root config at its existing position.
      const currentMapLayerConfigs = this.getMapViewer().mapFeaturesConfig.map.listOfGeoviewLayerConfig;
      if (currentMapLayerConfigs && originalMapConfigIndex >= 0) {
        const currentConfigIndex = currentMapLayerConfigs.findIndex((config) => config.geoviewLayerId === rootLayerId);
        if (currentConfigIndex >= 0) currentMapLayerConfigs.splice(currentConfigIndex, 1);
        currentMapLayerConfigs.splice(Math.min(originalMapConfigIndex, currentMapLayerConfigs.length), 0, structuredClone(configSnapshot));
      }

      // Registration can move the row before asynchronous metadata loading completes.
      const { layer, promiseLayer } = this.addGeoviewLayer(structuredClone(configSnapshot));
      this.#restoreOrderedLayerPaths(rootLayerId, originalOrderedPaths, originalRootPaths);

      let reloadSucceeded = false;
      try {
        await promiseLayer;
        reloadSucceeded = layer.getLayerLoadErrors().length === 0;
      } catch (error: unknown) {
        // addGeoviewLayer already reports load failures through its promise handler.
        logger.logPromiseFailed(`Reload failed for GeoView layer ${rootLayerId}`, error);
      }

      // Reconcile the immediate restore only if refreshed metadata changed the layer's sublayer tree.
      const refreshedPaths = AbstractMapViewerController.generateOrderedLayerPaths(layer.getGeoviewLayerConfig());
      const refreshedPathsDiffer =
        refreshedPaths.length !== originalRootPaths.length || refreshedPaths.some((path, index) => path !== originalRootPaths[index]);
      if (refreshedPathsDiffer) {
        this.#restoreOrderedLayerPaths(rootLayerId, originalOrderedPaths, refreshedPaths.length ? refreshedPaths : originalRootPaths);
      }

      if (reloadSucceeded) {
        this.getMapViewer().notifications.showSuccess('layers.layerReloaded', { layerName: layer.getGeoviewLayerName() });
      }
    } catch (error: unknown) {
      this.#geoviewLayerConfigSnapshots[rootLayerId] = structuredClone(configSnapshot);
      this.showLayerError(error, rootLayerId, configSnapshot.geoviewLayerName);
      this.#restoreOrderedLayerPaths(rootLayerId, originalOrderedPaths, originalRootPaths);
    }
  }

  /**
   * Reloads only a single errored child entry when its parent group and siblings are healthy.
   *
   * Rebuilds just the errored leaf by reusing the owning GeoView layer's already-fetched metadata and slotting the
   * recreated GV layer back into its existing parent group, so loaded siblings are never torn down. Records and
   * reports a reprocessing failure without disturbing healthy siblings. Falls through (returns false) when the
   * scenario does not apply, letting the caller rebuild the whole GeoView layer instead.
   *
   * @param layerPath - The path to the errored child to reload
   * @returns A promise that resolves with true when the child reload was handled, false to fall back to a full reload
   */
  async #tryReloadSingleErroredChild(layerPath: string): Promise<boolean> {
    const { layerController } = this.getControllersRegistry();
    const layerEntryConfig = layerController.getLayerEntryConfigIfExists(layerPath);

    // Only applies to an errored leaf entry whose parent group is not itself in error.
    if (!layerEntryConfig || layerEntryConfig.layerStatus !== 'error' || layerEntryConfig.getEntryTypeIsGroup()) return false;
    if (!(layerEntryConfig instanceof AbstractBaseLayerEntryConfig)) return false;
    const parentConfig = layerEntryConfig.getParentLayerConfig();
    if (!parentConfig || parentConfig.layerStatus === 'error') return false;

    // The owning GeoView layer must still exist with its fetched metadata to reuse.
    const geoviewLayer = this.#geoviewLayers[layerEntryConfig.getGeoviewLayerId()];
    if (!geoviewLayer || !geoviewLayer.getMetadata()) return false;

    // The parent group GV layer must be on the map to receive the recreated child.
    const parentGVLayer = layerController.getGeoviewLayerIfExists(parentConfig.layerPath);
    if (!(parentGVLayer instanceof GVGroupLayer)) return false;

    // Remove any stale GV layer for this exact path before recreating it, keeping its config registered.
    const existingGVLayer = layerController.getGeoviewLayerIfExists(layerPath);
    if (existingGVLayer) {
      const existingOLLayer = existingGVLayer.getOLLayer();
      if (existingOLLayer) parentGVLayer.getOLLayer().getLayers().remove(existingOLLayer);
      this.#layerDomain.deleteGVLayer(existingGVLayer);
    }

    // Reprocess just this entry, reusing the already-fetched service metadata.
    let gvLayer: AbstractGVLayer | undefined;
    try {
      gvLayer = await geoviewLayer.reprocessOneLayerEntry(
        layerEntryConfig,
        this.#uiDomain.getDisplayDateMode(),
        this.getMapViewer().getProjection()
      );
    } catch (error: unknown) {
      // Reprocessing is best-effort here; retain the same layer-error state while keeping healthy siblings in place.
      const loadError = error instanceof PromiseRejectErrorWrapper ? error.error : formatError(error);
      const failedConfig =
        error instanceof PromiseRejectErrorWrapper && error.object instanceof ConfigBaseClass ? error.object : layerEntryConfig;
      geoviewLayer.addLayerLoadError(loadError, failedConfig);
    }

    // The entry is still invalid; report it and stop (do not tear down the healthy siblings with a full rebuild).
    if (!gvLayer) {
      this.showLayerError(
        new LayerFailedToLoadError(layerEntryConfig.getLayerNameCascade()),
        layerEntryConfig.getGeoviewLayerId(),
        layerEntryConfig.getLayerNameCascade()
      );
      return true;
    }

    // Slot the recreated child into its parent group and refresh the z-order.
    parentGVLayer.addLayer(gvLayer);
    layerController.setLayerZIndices();

    // Show success of the layer reload.
    this.getMapViewer().notifications.showSuccess('layers.layerReloaded', { layerName: gvLayer.getLayerName() });
    return true;
  }

  /**
   * Removes all geoview layers from the map.
   */
  removeAllGeoviewLayers(): void {
    this.getControllersRegistry()
      .layerController.getLayerEntryLayerPaths()
      .forEach((layerEntryConfigId) => {
        // Remove it
        this.removeLayerUsingPath(layerEntryConfigId);
      });
  }

  /**
   * Removes a layer from the map using its layer path. The path may point to the root geoview layer
   * or a sub layer.
   *
   * @param layerPath - The path or ID of the layer to be removed
   */
  removeLayerUsingPath(layerPath: string): void {
    // Get the layer controller
    const { layerController } = this.getControllersRegistry();

    // Remove any highlights associated with the layer
    layerController.removeLayerHighlights(layerPath);

    // A layer path is a slash seperated string made of the GeoView layer Id followed by the layer Ids
    const layerPathNodes = layerPath.split('/');

    // Get the layer entry config to remove
    const layerEntryConfig = layerController.getLayerEntryConfigIfExists(layerPath);

    // If the layer config was found
    if (layerEntryConfig) {
      // initialize these two constant now because we will delete the information used to get their values.
      const indexToDelete = layerEntryConfig
        ? layerEntryConfig.getParentLayerConfig()?.listOfLayerEntryConfig.findIndex((layerConfig) => layerConfig === layerEntryConfig)
        : undefined;
      const listOfLayerEntryConfigAffected = layerController
        .getLayerEntryConfigIfExists(layerPath)
        ?.getParentLayerConfig()?.listOfLayerEntryConfig;

      // Remove layer info from registered layers
      layerController.getLayerEntryLayerPaths().forEach((registeredLayerPath) => {
        if (registeredLayerPath.startsWith(`${layerPath}/`) || registeredLayerPath === layerPath) {
          // Get the geoview layer if exists
          const innerGVLayer = layerController.getGeoviewLayerIfExists(registeredLayerPath);

          // Remove actual OL layer from the map
          const layer = innerGVLayer?.getOLLayer();
          if (layer) this.getMapViewer().map.removeLayer(layer);

          // Unregister layer config from the application
          this.#unregisterLayerConfig(layerController.getLayerEntryConfig(registeredLayerPath));

          // Remove the text layer if it is a vector layer
          if (innerGVLayer instanceof AbstractGVVector) {
            const textLayer = innerGVLayer.getTextOLLayer();
            if (textLayer) this.getMapViewer().map.removeLayer(textLayer);
          }

          // Unregister from the domain
          if (innerGVLayer) this.#layerDomain.deleteGVLayer(innerGVLayer);

          // Remove from registered geoviewLayers
          delete this.#geoviewLayers[registeredLayerPath];
        }
      });

      // Now that some layers have been removed, check if they are all effectively loaded/error and update store if so
      layerController.checkIfAllLayersLoadedAndUpdateStore();

      // Remove from parents listOfLayerEntryConfig
      if (listOfLayerEntryConfigAffected) listOfLayerEntryConfigAffected.splice(indexToDelete!, 1);

      // Remove layer from geoview layers
      if (this.#geoviewLayers[layerPathNodes[0]]) {
        const geoviewLayer = this.#geoviewLayers[layerPathNodes[0]];

        // If it is a single layer, remove geoview layer
        if (layerPathNodes.length === 1 || (layerPathNodes.length === 2 && geoviewLayer.listOfLayerEntryConfig.length === 1)) {
          // Dispose of the root layer reference
          geoviewLayer.olRootLayer?.dispose();
          if (geoviewLayer.olRootLayer) delete geoviewLayer.olRootLayer;

          delete this.#geoviewLayers[layerPathNodes[0]];
          delete this.#geoviewLayerConfigSnapshots[layerPathNodes[0]];
          const { mapFeaturesConfig } = this.getMapViewer();

          if (mapFeaturesConfig.map.listOfGeoviewLayerConfig)
            mapFeaturesConfig.map.listOfGeoviewLayerConfig = mapFeaturesConfig.map.listOfGeoviewLayerConfig.filter(
              (geoviewLayerConfig) => geoviewLayerConfig.geoviewLayerId !== layerPath
            );
        } else if (layerPathNodes.length === 2) {
          const updatedListOfLayerEntryConfig = geoviewLayer.listOfLayerEntryConfig.filter(
            (entryConfig) => entryConfig.layerId !== layerPathNodes[1]
          );
          geoviewLayer.listOfLayerEntryConfig = updatedListOfLayerEntryConfig;
        } else {
          // For layer paths more than two deep, drill down through listOfLayerEntryConfigs to layer entry config to remove
          let layerEntryConfig2 = geoviewLayer.listOfLayerEntryConfig.find((entryConfig) => entryConfig.layerId === layerPathNodes[1]);

          for (let i = 1; i < layerPathNodes.length; i++) {
            if (i === layerPathNodes.length - 1 && layerEntryConfig2) {
              // When we get to the top level, remove the layer entry config
              const updatedListOfLayerEntryConfig = layerEntryConfig2.listOfLayerEntryConfig.filter(
                (entryConfig) => entryConfig.layerId !== layerPathNodes[i]
              );
              geoviewLayer.listOfLayerEntryConfig = updatedListOfLayerEntryConfig;
            } else if (layerEntryConfig2) {
              // Not on the top level, so update to the latest
              layerEntryConfig2 = layerEntryConfig2.listOfLayerEntryConfig.find((entryConfig) => entryConfig.layerId === layerPathNodes[i]);
            }
          }
        }
      }

      // Emit about it
      this.#emitLayerConfigRemoved({ layerPath, layerName: layerEntryConfig.getLayerNameCascade() });

      // Log
      logger.logInfo(`Layer removed for ${layerPath}`);

      // Clear the selected path if it was set to it
      const currentlySelectedLayerPath = getStoreLayerSelectedLayerPath(this.getMapId());
      if (layerPath === currentlySelectedLayerPath) {
        // Clear it
        setStoreLayerSelectedLayersTabLayer(this.getMapId(), '');
      }

      // Redirect to feature info delete
      this.getControllersRegistry().detailsController.deleteFeatureInfo(layerPath);
    }
  }

  /**
   * Show the errors that happened during layers loading.
   *
   * If it's an aggregate error, log and show all of them.
   * If it's a regular error, log and show only that error.
   *
   * @param error - The error to log and show
   * @param geoviewLayerId - The Geoview layer id for which the error happened
   * @param geoviewLayerName - Optional friendly layer name (as shown in the legend) used for the user-facing message
   */
  showLayerError(error: unknown, geoviewLayerId: string, geoviewLayerName?: string): void {
    // If an aggregation error
    if (error instanceof AggregateError) {
      // For each errors
      error.errors.forEach((layerError) => {
        // Recursive call
        this.showLayerError(layerError, geoviewLayerId, geoviewLayerName);
      });
    } else {
      // Cast the error
      const theError = formatError(error);

      // Read the layer path if possible, more precise. Resolve a friendly display name for the user message,
      // preferring the provided name, then the config's cascaded name, then falling back to the id.
      let layerPathOrId = geoviewLayerId;
      let displayName = geoviewLayerName || geoviewLayerId;
      if (theError instanceof LayerEntryConfigError) {
        layerPathOrId = theError.layerConfig.layerPath;
        displayName = geoviewLayerName || theError.layerConfig.getLayerNameCascade();
      }

      // Ensure the user always gets a specific, translated message. If the error isn't already a typed
      // GeoViewError, wrap it in a LayerFailedToLoadError (keeping the raw error as cause) so we avoid the
      // misleading generic "contact us / view console" failover.
      let errorToShow: GeoViewError;
      if (theError instanceof GeoViewError) {
        errorToShow = theError;
      } else {
        // Log the underlying error for developers, then surface a specific layer message to the user
        logger.logError(theError);
        errorToShow = new LayerFailedToLoadError(displayName, theError);
      }

      // Show error
      this.getMapViewer().notifications.showErrorFromError(errorToShow);

      // Emit about it using the translated message
      this.#emitLayerConfigError({
        layerPath: layerPathOrId,
        error: errorToShow.translateMessage(this.getMapViewer().getDisplayLanguage()),
      });
    }
  }

  // #region PRIVATE METHODS

  /**
   * Rewrites a configured layers-panel selection that references only a layer's root id to its first resolved path.
   *
   * The config can only reference a GeoCore layer by its UUID; once the sublayer structure resolves the real path is
   * `uuid/<layerId>`, so the bare root id would never match an entry in the layers panel. This updates the app bar and
   * footer bar `selectedLayersLayerPath` in place when they still point at the given root id.
   *
   * @param rootId - The layer's root id (e.g. a GeoCore UUID)
   * @param firstLayerPath - The first resolved layer path under that root id
   */
  #resolveConfiguredSelectedLayersLayerPath(rootId: string, firstLayerPath: string): void {
    const { footerBar, appBar } = this.getMapViewer().mapFeaturesConfig;
    if (footerBar?.selectedLayersLayerPath === rootId) footerBar.selectedLayersLayerPath = firstLayerPath;
    if (appBar?.selectedLayersLayerPath === rootId) appBar.selectedLayersLayerPath = firstLayerPath;
  }

  /**
   * Replaces the paths for one GeoView layer without changing the relative order of other layers.
   *
   * @param rootLayerId - The owning GeoView layer id
   * @param originalOrderedPaths - The ordered paths captured before reload
   * @param replacementPaths - The newly generated paths, or the prior paths when creation failed
   */
  #restoreOrderedLayerPaths(rootLayerId: string, originalOrderedPaths: string[], replacementPaths: string[]): void {
    const belongsToRoot = (path: string): boolean => path === rootLayerId || path.startsWith(`${rootLayerId}/`);
    const firstRootIndex = originalOrderedPaths.findIndex(belongsToRoot);
    const insertionIndex = firstRootIndex < 0 ? originalOrderedPaths.length : firstRootIndex;
    const orderedPaths = originalOrderedPaths.filter((path) => !belongsToRoot(path));

    orderedPaths.splice(insertionIndex, 0, ...replacementPaths);
    this.getControllersRegistry().layerController.setMapOrderedLayersDirectly(orderedPaths);
  }

  /**
   * Continues the addition of the geoview layer.
   *
   * @param geoviewLayerConfig - The geoview layer configuration to add
   * @param abortSignal - Optional {@link AbortSignal} used to cancel the layer creation process
   * @returns The result of the addition of the geoview layer
   * The result contains the instanciated GeoViewLayer along with a promise that will resolve when the layer will be officially on the map.
   */
  #addGeoviewLayerStep2(geoviewLayerConfig: TypeGeoviewLayerConfig, abortSignal?: AbortSignal): GeoViewLayerAddedResult {
    // Create the layer for the processing
    const layerBeingAdded = LayerCreatorController.createLayerConfigFromType(geoviewLayerConfig);

    // Set the map-level service URLs configuration so the layer knows service urls to use
    const { serviceUrls } = this.getMapViewer().mapFeaturesConfig;
    if (serviceUrls) layerBeingAdded.setConfigServiceUrls(serviceUrls);

    // Add in the geoviewLayers set
    this.#geoviewLayers[layerBeingAdded.getGeoviewLayerId()] = layerBeingAdded;

    // For each layer entry config in the geoview layer
    layerBeingAdded.getAllLayerEntryConfigs().forEach((layerConfig) => {
      // Log
      logger.logTraceCore(`LAYERS - 1 - Registering layer entry config ${layerConfig.layerPath} on map ${this.getMapId()}`, layerConfig);

      // Register it
      this.#registerLayerConfigInit(layerConfig);

      // Add filters to map initial filters, if they exist
      this.#addInitialFilters(layerConfig);
    });

    // Register a callback when the layer entry config wants to register extra configs
    layerBeingAdded.onLayerEntryRegisterInit(this.#handleLayerEntryRegisterInit.bind(this));

    // Register a callback when layer wants to send a message
    layerBeingAdded.onLayerMessage(this.#handleLayerMessage.bind(this));

    // Register a callback when a Group Layer has been created
    layerBeingAdded.onLayerGroupCreated(this.#handleLayerGroupCreated.bind(this));

    // Register a callback when a GV Layer has been created
    layerBeingAdded.onLayerGVCreated(this.#handleLayerGVCreated.bind(this));

    // Create a promise that the layer will be added on the map
    const promiseLayer = new Promise<void>((resolve, reject) => {
      // Continue the addition process
      layerBeingAdded
        .createGeoViewLayers(this.#uiDomain.getDisplayDateMode(), this.getMapViewer().getProjection(), abortSignal)
        .then(() => {
          // Add the layer on the map
          this.#addToMap(layerBeingAdded);

          // If there were partial errors (some sub-layers failed but valid ones were added), report them
          const partialErrors = layerBeingAdded.getLayerLoadErrors();
          if (partialErrors.length > 0) {
            const error = partialErrors.length === 1 ? partialErrors[0] : new AggregateError(partialErrors);
            this.showLayerError(error, geoviewLayerConfig.geoviewLayerId, geoviewLayerConfig.geoviewLayerName);
          }

          // Resolve, done
          resolve();

          // Emit
          this.#emitLayerConfigAdded({ layer: layerBeingAdded });
        })
        .catch((error: unknown) => {
          // Reject it higher, because that's not where we want to handle the promise failure, we're returning the promise higher
          reject(formatError(error));
        });
    });

    // Return the layer with the promise it'll be on the map
    return { layer: layerBeingAdded, promiseLayer };
  }

  /**
   * Registers the layer identifier.
   *
   * @param layerConfig - The layer entry config to register
   */
  #registerLayerConfigInit(layerConfig: ConfigBaseClass): void {
    // Register it in the domain
    this.#layerDomain.registerLayerEntryConfig(layerConfig);
  }

  /**
   * Merges time-slider configurations from GeoCore into the map's corePackagesConfig.
   *
   * The configs are stored for the time-slider plugin to pick up if it's configured in the footer bar.
   *
   * @param timeSliderConfigs - The time-slider configurations returned from GeoCore
   */
  mergeTimeSliderConfigsIntoCorePackages(timeSliderConfigs: GeoViewTimeSliderConfig[]): void {
    // Get the current corePackagesConfig from the map config
    const mapConfig = this.getMapViewer().mapFeaturesConfig;

    // Initialize corePackagesConfig if not present
    if (!mapConfig.corePackagesConfig) {
      mapConfig.corePackagesConfig = [];
    }

    // For each time-slider config from GeoCore
    timeSliderConfigs.forEach((tsConfig) => {
      // Find existing time-slider entry in corePackagesConfig
      const existingEntry = mapConfig.corePackagesConfig!.find((pkg) => Object.keys(pkg).includes('time-slider'));

      if (existingEntry) {
        // Merge sliders into existing entry
        const existingSliders = (existingEntry['time-slider'] as Record<string, unknown>)?.sliders;
        if (Array.isArray(existingSliders) && Array.isArray(tsConfig.sliders)) {
          (existingSliders as Record<string, unknown>[]).push(...tsConfig.sliders);
        } else if (tsConfig.sliders) {
          (existingEntry['time-slider'] as Record<string, unknown>).sliders = tsConfig.sliders;
        }
      } else {
        // Add new time-slider entry
        mapConfig.corePackagesConfig!.push({ 'time-slider': tsConfig });
      }
    });

    // Log
    logger.logInfo('Merged time-slider configs from GeoCore into corePackagesConfig');
  }

  /**
   * Unregisters the layer in the Domain to stop managing it.
   *
   * @param layerConfig - The layer entry config to unregister
   * @param unregisterOrderedLayer - Should it be unregistered from orderedLayers
   */
  #unregisterLayerConfig(layerConfig: ConfigBaseClass, unregisterOrderedLayer = true): void {
    // Unregister from ordered layers
    if (unregisterOrderedLayer) {
      // Remove from ordered layers
      this.getControllersRegistry().layerController.removeOrderedLayerPath(layerConfig.layerPath);
    }

    // If the TimeSlider plugin is initialized
    if (isStoreTimeSliderInitialized(this.getMapId())) {
      // Remove from the TimeSlider
      removeStoreTimeSliderLayer(this.getMapId(), layerConfig.layerPath, () => {
        // Remove the tab
        this.getControllersRegistry().uiController.hideTabButton('time-slider');
      });
    }

    // If the geochart plugin is initialized
    if (isStoreGeochartInitialized(this.getMapId())) {
      // Remove from the GeoChart Charts
      this.getControllersRegistry().geoChartController?.removeChart(layerConfig.layerPath, () => {
        // Remove the tab
        this.getControllersRegistry().uiController.hideTabButton('geochart');
      });
    }

    // If the swiper plugin is initialized
    if (isStoreSwiperInitialized(this.getMapId())) {
      // Remove it from the Swiper
      this.getControllersRegistry().swiperController?.removeLayerPathIfExists(layerConfig.layerPath);
    }

    // Unregister from the domain
    this.#layerDomain.unregisterLayerEntryConfig(layerConfig);
  }

  /**
   * Gets all child paths from a parent path.
   *
   * @param parentPath - The parent path
   * @returns Child layer paths
   * @throws {LayerConfigNotFoundError} When the layer configuration couldn't be found at the given layer path
   * @throws {LayerWrongTypeError} When the layer configuration is of the wrong type at the given layer path
   */
  #getAllChildPaths(parentPath: string): string[] {
    // Get the group layer
    const parentLayerEntryConfig = this.getControllersRegistry().layerController.getLayerEntryConfigGroup(parentPath);

    // Return all the layer paths inside that group
    return parentLayerEntryConfig.getLayerPathsAll();
  }

  /**
   * Handles the initialization of a layer-entry registration event.
   *
   * This method is triggered when an additional layer-entry configuration
   * (typically created dynamically) needs to be registered in the map's
   * layer configuration system.
   *
   * Behavior:
   *  1. Checks whether a configuration for the given `layerPath` already exists.
   *  2. If it exists, unregisters the old configuration (without triggering
   *     cleanup actions tied to removal).
   *  3. Registers the new layer-entry configuration using `registerLayerConfigInit`.
   *
   * @param sender - The GeoView layer associated with this registration event
   * @param event - The event containing the layer-entry configuration to be registered
   */
  #handleLayerEntryRegisterInit(sender: AbstractGeoViewLayer, event: LayerEntryRegisterInitEvent): void {
    // Log
    logger.logTraceCore(
      `LAYERS - 1.5 - Registering an extra layer entry config ${event.config.layerPath} on map ${this.getMapId()}`,
      event.config
    );

    // If already existing
    const alreadyExisting = this.getControllersRegistry().layerController.getLayerEntryConfigIfExists(event.config.layerPath);
    if (alreadyExisting) {
      // Unregister the old one
      this.#unregisterLayerConfig(alreadyExisting, false);
    }

    // Register it
    this.#registerLayerConfigInit(event.config);
  }

  /**
   * Handles the creation of a GeoView layer (`GVLayer`) after its underlying
   * OL layer and configuration have been fully initialized.
   *
   * This method is triggered once a layer has completed its construction,
   * allowing the system to register it, attach handlers, and notify any
   * listeners that the layer is now ready for interaction.
   *
   * Behavior:
   *  1. Stores references to the GV layer and the underlying OL layer,
   *     indexed by their `layerPath`.
   *  2. Registers internal event handlers for the new layer.
   *  3. Emits a "layer created" event so external code can bind to it immediately.
   *  4. Calls the layer's `init()` method to finalize initialization.
   *
   * @param sender - The parent or context GeoView layer associated with this creation event
   * @param event - The event containing the newly created GV layer instance and its configuration
   */
  #handleLayerGVCreated(sender: AbstractGeoViewLayer, event: LayerGVCreatedEvent): void {
    // Get the GV Layer and the config
    const gvLayer = event.layer;
    const layerConfig = gvLayer.getLayerConfig();

    // Log
    logger.logTraceCore(
      `LAYERS - 9 - GV Layer created for ${layerConfig.layerPath} on map ${this.getMapId()}`,
      layerConfig.layerStatus,
      layerConfig
    );

    // Register in the domain
    this.#layerDomain.registerGVLayer(gvLayer);

    // Handle text layer for vector layers
    if (gvLayer instanceof AbstractGVVector) {
      const textLayer = gvLayer.getTextOLLayer();
      if (textLayer) {
        this.getMapViewer().map.addLayer(textLayer);
      }
    }

    // Emit about its creation so that one can attach events on it right away if necessary
    this.#emitLayerCreated({ layer: gvLayer });
  }

  /**
   * Handles the creation of a GeoView group layer (`GVGroupLayer`).
   *
   * This method is invoked once a group layer has been fully instantiated,
   * allowing the system to register it, attach handlers, and initialize its
   * visibility constraints.
   *
   * Behavior:
   *  1. Stores references to the GV group layer and its corresponding
   *     OpenLayers layer, indexed by `layerPath`.
   *  2. Registers internal event handlers specific to group layers.
   *  3. Computes and stores the layer's initial "in visible range" state.
   *
   * @param sender - The parent or context layer associated with this creation event.
   * @param event - The event containing the newly created group layer instance and its configuration.
   */
  #handleLayerGroupCreated(sender: AbstractGeoViewLayer, event: LayerGroupCreatedEvent): void {
    // Get the Group Layer and the config
    const groupLayer = event.layer;
    const layerConfig = groupLayer.getLayerConfig();

    // Log
    logger.logTraceCore(
      `LAYERS - 7 - Group Layer created for ${layerConfig.layerPath} on map ${this.getMapId()}`,
      layerConfig.layerStatus,
      layerConfig
    );

    // Register in the domain
    this.#layerDomain.registerGVLayer(groupLayer);
  }

  /**
   * Handles layer-specific messages and displays them through the map viewer's notification system.
   *
   * @param sender - The layer instance that triggered the message
   * @param layerMessageEvent - The message event containing notification details
   *
   * @example
   * handleLayerMessage(myLayer, {
   *   messageKey: 'layers.fetchProgress',
   *   messageParams: { processed: 50, total: 100 },
   *   messageType: 'error',
   * });
   */
  #handleLayerMessage(sender: AbstractGeoViewLayer, layerMessageEvent: LayerMessageEvent): void {
    // Read event params for clarity
    const { messageType } = layerMessageEvent;
    const { messageKey } = layerMessageEvent;
    const { messageParams } = layerMessageEvent;

    if (messageType === 'info') {
      this.getMapViewer().notifications.showMessage(messageKey, messageParams);
    } else if (messageType === 'warning') {
      this.getMapViewer().notifications.showWarning(messageKey, messageParams);
    } else if (messageType === 'error') {
      this.getMapViewer().notifications.showError(messageKey, messageParams);
    } else if (messageType === 'success') {
      this.getMapViewer().notifications.showSuccess(messageKey, messageParams);
    }
  }

  /**
   * Continues the addition of the geoview layer.
   * Adds the layer to the map if valid. If not (is a string) emits an error.
   *
   * @param geoviewLayer - The layer
   */
  #addToMap(geoviewLayer: AbstractGeoViewLayer): void {
    // If no root layer is set, forget about it
    if (!geoviewLayer.olRootLayer) return;

    // If all layer status are good
    if (!geoviewLayer.allLayerStatusAreGreaterThanOrEqualTo('error')) {
      // Add the OpenLayers layer to the map officially
      this.getMapViewer().map.addLayer(geoviewLayer.olRootLayer);

      // Log
      logger.logInfo(`GeoView Layer ${geoviewLayer.getGeoviewLayerId()} added to map ${this.getMapId()}`, geoviewLayer);

      // Set the layer z indices
      this.getControllersRegistry().layerController.setLayerZIndices();
    }
  }

  /**
   * Adds initial filters to layers, if provided.
   *
   * @param layerConfig - The layer config being processed
   */
  #addInitialFilters(layerConfig: ConfigBaseClass): void {
    // If correct subclass, otherwise skip
    if (layerConfig instanceof AbstractBaseLayerEntryConfig) {
      // Get the layer filter
      const layerFilter = layerConfig.getLayerFilter();

      // If any layer filter
      if (layerFilter) {
        // Save to the store
        addStoreLayerInitialFilter(this.getMapId(), layerConfig.layerPath, layerFilter);
      }
    }
  }

  /**
   * Validates the geoview layer configuration array to eliminate duplicate entries and inform the user.
   *
   * @param mapConfigLayerEntries - The Map Config Layer Entries to validate
   * @returns The new configuration with duplicate entries eliminated
   */
  #deleteDuplicateAndMultipleUuidGeoviewLayerConfig(mapConfigLayerEntries?: MapConfigLayerEntry[]): MapConfigLayerEntry[] {
    if (mapConfigLayerEntries && mapConfigLayerEntries.length > 0) {
      const validGeoviewLayerConfigs = mapConfigLayerEntries.filter((geoviewLayerConfigToCreate, configToCreateIndex) => {
        for (let configToTestIndex = 0; configToTestIndex < mapConfigLayerEntries.length; configToTestIndex++) {
          if (
            geoviewLayerConfigToCreate.geoviewLayerId === mapConfigLayerEntries[configToTestIndex].geoviewLayerId &&
            // We keep the first instance of the duplicate entry.
            configToCreateIndex > configToTestIndex
          ) {
            this.#printDuplicateGeoviewLayerConfigError(geoviewLayerConfigToCreate);

            // Remove geoCore ordered layer placeholder
            this.getControllersRegistry().layerController.removeOrderedLayerPath(geoviewLayerConfigToCreate.geoviewLayerId, false);

            return false;
          }
        }
        return true;
      });
      return validGeoviewLayerConfigs;
    }
    return [];
  }

  /**
   * Prints an error message for the duplicate geoview layer configuration.
   *
   * @param mapConfigLayerEntry - The Map Config Layer Entry in error
   */
  #printDuplicateGeoviewLayerConfigError(mapConfigLayerEntry: MapConfigLayerEntry): void {
    // Log
    logger.logError(`Duplicate use of geoview layer identifier ${mapConfigLayerEntry.geoviewLayerId} on map ${this.getMapId()}`);

    // Show the error
    this.getMapViewer().notifications.showError('validation.layer.usedtwice', { layerId: mapConfigLayerEntry.geoviewLayerId });
  }

  // #endregion PRIVATE METHODS

  // #region STATIC METHODS

  /**
   * Creates an instance of a specific `AbstractGeoViewLayer` subclass based on the given GeoView layer configuration.
   *
   * This function determines the correct layer type from the configuration and instantiates it accordingly.
   * Supports GeoJSON, CSV, WMS, Esri Dynamic, Esri Feature, Esri Image, GeoTIFF, ImageStatic, KML, WFS, WKB,
   * OGC Feature, XYZ Tiles, and Vector Tiles. Throws if the layer type is unsupported.
   *
   * TODO: Refactor to use the validated configuration with metadata already fetched.
   *
   * @param geoviewLayerConfig - The configuration object for the GeoView layer
   * @returns An instance of the corresponding `AbstractGeoViewLayer` subclass
   * @throws {NotSupportedError} When the configuration does not match any supported layer type
   */
  static createLayerConfigFromType(geoviewLayerConfig: TypeGeoviewLayerConfig): AbstractGeoViewLayer {
    // Depending on the layer type of config
    if (CsvLayerEntryConfig.isClassOrTypeCSV(geoviewLayerConfig)) {
      return new CSV(geoviewLayerConfig);
    }
    if (EsriDynamicLayerEntryConfig.isClassOrTypeEsriDynamic(geoviewLayerConfig)) {
      return new EsriDynamic(geoviewLayerConfig);
    }
    if (EsriFeatureLayerEntryConfig.isClassOrTypeEsriFeature(geoviewLayerConfig)) {
      return new EsriFeature(geoviewLayerConfig);
    }
    if (EsriImageLayerEntryConfig.isClassOrTypeEsriImage(geoviewLayerConfig)) {
      return new EsriImage(geoviewLayerConfig);
    }
    if (GeoJSONLayerEntryConfig.isClassOrTypeGeoJSON(geoviewLayerConfig)) {
      return new GeoJSON(geoviewLayerConfig);
    }
    if (GeoTIFFLayerEntryConfig.isClassOrTypeGeoTIFF(geoviewLayerConfig)) {
      return new GeoTIFF(geoviewLayerConfig);
    }
    if (ImageStaticLayerEntryConfig.isClassOrTypeImageStatic(geoviewLayerConfig)) {
      return new ImageStatic(geoviewLayerConfig);
    }
    if (KmlLayerEntryConfig.isClassOrTypeKMLLayer(geoviewLayerConfig)) {
      return new KML(geoviewLayerConfig);
    }
    if (OgcFeatureLayerEntryConfig.isClassOrTypeOGCLayer(geoviewLayerConfig)) {
      return new OgcFeature(geoviewLayerConfig);
    }
    if (VectorTilesLayerEntryConfig.isClassOrTypeVectorTiles(geoviewLayerConfig)) {
      return new VectorTiles(geoviewLayerConfig);
    }
    if (OgcWfsLayerEntryConfig.isClassOrTypeWFSLayer(geoviewLayerConfig)) {
      return new WFS(geoviewLayerConfig);
    }
    if (WkbLayerEntryConfig.isClassOrTypeWKBLayer(geoviewLayerConfig)) {
      return new WKB(geoviewLayerConfig);
    }
    if (OgcWmsLayerEntryConfig.isClassOrTypeWMS(geoviewLayerConfig)) {
      return new WMS(geoviewLayerConfig);
    }
    if (OgcWmtsLayerEntryConfig.isClassOrTypeWMTS(geoviewLayerConfig)) {
      return new WMTS(geoviewLayerConfig);
    }
    if (XYZTilesLayerEntryConfig.isClassOrTypeXYZTiles(geoviewLayerConfig)) {
      return new XYZTiles(geoviewLayerConfig);
    }

    // Not implemented
    throw new NotSupportedError('Unsupported layer class type in createLayerConfigFromType');
  }

  /**
   * Converts a list of map configuration layer entries into an array of promises,
   * each resolving to one or more GeoView layer configuration objects.
   *
   * @param mapId - The unique identifier of the map instance this configuration applies to
   * @param language - The language setting used for layer labels and metadata
   * @param mapConfigLayerEntries - The array of layer entries to convert
   * @param addGeoChartCallback - Callback invoked when a geochart configuration is initialized during layer processing
   * @param addTimeSliderCallback - Callback invoked when time-slider configurations are found during layer processing
   * @param errorCallback - Callback invoked when an error occurs during layer processing
   * @returns An array of promises, each resolving to a TypeGeoviewLayerConfig object
   */
  static convertMapConfigsToGeoviewLayerConfig(
    mapId: string,
    currentLayerIds: string[],
    language: TypeDisplayLanguage,
    mapConfigLayerEntries: MapConfigLayerEntry[],
    addGeoChartCallback: (layerPath: string, geochartConfig: GeoViewGeoChartConfig) => void,
    addTimeSliderCallback: (timeSliderConfigs: GeoViewTimeSliderConfig[]) => void,
    errorCallback: (mapConfigLayerEntry: MapConfigLayerEntry, error: unknown) => void
  ): Promise<TypeGeoviewLayerConfig>[] {
    // For each layer entry
    return mapConfigLayerEntries.map((entry) => {
      // Redirect
      return this.convertMapConfigToGeoviewLayerConfig(
        mapId,
        currentLayerIds,
        language,
        entry,
        addGeoChartCallback,
        addTimeSliderCallback,
        errorCallback
      );
    });
  }

  /**
   * Converts a map configuration layer entry into a promise of a GeoView layer configuration.
   *
   * Depending on the type of the layer entry (e.g., GeoCore, GeoPackage, Shapefile, RCS, or standard GeoView),
   * this function processes each entry accordingly and wraps the result in a `Promise`.
   * Errors encountered during asynchronous operations are handled via a provided callback.
   *
   * @param mapId - The unique identifier of the map instance this configuration applies to
   * @param language - The language setting used for layer labels and metadata
   * @param entry - The array of layer entry to convert
   * @param addGeoChartCallback - Callback invoked when a geochart configuration is initialized during layer processing
   * @param addTimeSliderCallback - Callback invoked when time-slider configurations are found during layer processing
   * @param errorCallback - Callback invoked when an error occurs during layer processing
   * @returns A promise that resolves to a TypeGeoviewLayerConfig object
   */
  static convertMapConfigToGeoviewLayerConfig(
    mapId: string,
    currentLayerIds: string[],
    language: TypeDisplayLanguage,
    entry: MapConfigLayerEntry,
    addGeoChartCallback: (layerPath: string, geochartConfig: GeoViewGeoChartConfig) => void,
    addTimeSliderCallback: (timeSliderConfigs: GeoViewTimeSliderConfig[]) => void,
    errorCallback: (mapConfigLayerEntry: MapConfigLayerEntry, error: unknown) => void
  ): Promise<TypeGeoviewLayerConfig> {
    // Depending on the map config layer entry type
    let promise: Promise<TypeGeoviewLayerConfig>;
    if (mapConfigLayerEntryIsGeoCore(entry)) {
      // Working with a GeoCore layer
      promise = GeoCore.createLayerConfigFromUUID(entry.geoviewLayerId, currentLayerIds, language, mapId, entry).then((response) => {
        // If a Geochart is initialized
        if (isStoreGeochartInitialized(mapId)) {
          // For each geocharts configuration
          Object.entries(response.geocharts).forEach(([layerPath, geochartConfig]) => {
            // Callback
            addGeoChartCallback(layerPath, geochartConfig);
          });
        }

        // If time-slider is initialized and time-slider configs were returned from GeoCore
        if (isStoreTimeSliderInitialized(mapId) && response.timeSliderConfigs.length > 0) {
          // Callback
          addTimeSliderCallback(response.timeSliderConfigs);
        }

        return response.config;
      });
    } else if (mapConfigLayerEntryIsGeoPackage(entry)) {
      // Working with a geopackage layer
      promise = GeoPackageReader.createLayerConfigFromGeoPackage(entry);
    } else if (mapConfigLayerEntryIsShapefile(entry)) {
      // Working with a shapefile layer
      promise = ShapefileReader.convertShapefileConfigToGeoJson(entry);
    } else if (mapConfigLayerEntryIsRCS(entry)) {
      // Working with a RCS (Geocore subset) layer
      promise = GeoCore.createLayerConfigFromRCSUUID(entry.geoviewLayerId, language, mapId);
    } else {
      // Working with a standard GeoView layer
      promise = Promise.resolve(entry);
    }

    // Prepare to catch errors
    promise.catch((error) => {
      // Callback
      errorCallback?.(entry, error);
    });

    return promise;
  }

  // #endregion STATIC METHODS

  // #region EVENTS

  /**
   * Emits an event to all handlers when a layer config has been added.
   *
   * @param event - The event to emit
   */
  #emitLayerConfigAdded(event: LayerBuilderEvent): void {
    // Emit the event for all handlers
    EventHelper.emitEvent(this, this.#onLayerConfigAddedHandlers, event);
  }

  /**
   * Registers a one-shot layer config added event handler that resolves a promise.
   *
   * @param filter - Optional filter predicate to skip non-matching events without unsubscribing
   * @returns A promise that resolves with the layer builder event
   */
  onceLayerConfigAdded(filter?: (event: LayerBuilderEvent) => boolean): Promise<LayerBuilderEvent> {
    return EventHelper.onceEventPromise(this.#onLayerConfigAddedHandlers, filter);
  }

  /**
   * Registers a layer config added event handler.
   *
   * @param callback - The callback to be executed whenever the event is emitted
   */
  onLayerConfigAdded(callback: LayerBuilderDelegate): void {
    // Register the event handler
    EventHelper.onEvent(this.#onLayerConfigAddedHandlers, callback);
  }

  /**
   * Unregisters a layer config added event handler.
   *
   * @param callback - The callback to stop being called whenever the event is emitted
   */
  offLayerConfigAdded(callback: LayerBuilderDelegate): void {
    // Unregister the event handler
    EventHelper.offEvent(this.#onLayerConfigAddedHandlers, callback);
  }

  /**
   * Emits an event to all handlers when a layer config has been flag as error.
   *
   * @param event - The event to emit
   */
  #emitLayerConfigError(event: LayerConfigErrorEvent): void {
    // Emit the event for all handlers
    EventHelper.emitEvent(this, this.#onLayerConfigErrorHandlers, event);
  }

  /**
   * Registers a layer config error event handler.
   *
   * @param callback - The callback to be executed whenever the event is emitted
   */
  onLayerConfigError(callback: LayerConfigErrorDelegate): void {
    // Register the event handler
    EventHelper.onEvent(this.#onLayerConfigErrorHandlers, callback);
  }

  /**
   * Unregisters a layer config error event handler.
   *
   * @param callback - The callback to stop being called whenever the event is emitted
   */
  offLayerConfigError(callback: LayerConfigErrorDelegate): void {
    // Unregister the event handler
    EventHelper.offEvent(this.#onLayerConfigErrorHandlers, callback);
  }

  /**
   * Emits an event to all handlers.
   *
   * @param event - The event to emit
   */
  #emitLayerConfigRemoved(event: LayerPathEvent): void {
    // Emit the event for all handlers
    EventHelper.emitEvent(this, this.#onLayerConfigRemovedHandlers, event);
  }

  /**
   * Registers a layer removed event handler.
   *
   * @param callback - The callback to be executed whenever the event is emitted
   */
  onLayerConfigRemoved(callback: LayerPathDelegate): void {
    // Register the event handler
    EventHelper.onEvent(this.#onLayerConfigRemovedHandlers, callback);
  }

  /**
   * Unregisters a layer removed event handler.
   *
   * @param callback - The callback to stop being called whenever the event is emitted
   */
  offLayerConfigRemoved(callback: LayerPathDelegate): void {
    // Unregister the event handler
    EventHelper.offEvent(this.#onLayerConfigRemovedHandlers, callback);
  }

  /**
   * Emits an event to all handlers.
   *
   * @param event - The event to emit
   */
  #emitLayerCreated(event: LayerEvent): void {
    // Emit the event for all handlers
    EventHelper.emitEvent(this, this.#onLayerCreatedHandlers, event);
  }

  /**
   * Registers a layer created event handler.
   *
   * @param callback - The callback to be executed whenever the event is emitted
   */
  onLayerCreated(callback: LayerDelegate): void {
    // Register the event handler
    EventHelper.onEvent(this.#onLayerCreatedHandlers, callback);
  }

  /**
   * Unregisters a layer created event handler.
   *
   * @param callback - The callback to stop being called whenever the event is emitted
   */
  offLayerCreated(callback: LayerDelegate): void {
    // Unregister the event handler
    EventHelper.offEvent(this.#onLayerCreatedHandlers, callback);
  }

  // #endregion EVENTS
}

/** Represents the result of adding a GeoView layer. */
export type GeoViewLayerAddedResult = {
  /** The created GeoView layer instance. */
  layer: AbstractGeoViewLayer;
  /** A promise that resolves when the layer is fully loaded. */
  promiseLayer: Promise<void>;
};

/** Defines the event payload for the layer loaded delegate. */
export interface LayerEvent {
  /** The loaded layer. */
  layer: AbstractGVLayer;
}

/** Defines a delegate for the layer loaded event handler function signature. */
export type LayerDelegate = EventDelegateBase<LayerCreatorController, LayerEvent, void>;

/** Defines the event payload for the layer path delegate. */
export interface LayerPathEvent {
  /** The layer path. */
  layerPath: string;

  /** The layer name. */
  layerName: string;
}

/** Defines a delegate for the layer path event handler function signature. */
export type LayerPathDelegate = EventDelegateBase<LayerCreatorController, LayerPathEvent, void>;

/** Defines the event payload for the layer builder delegate. */
export interface LayerBuilderEvent {
  /** The built layer. */
  layer: AbstractGeoViewLayer;
}

/** Defines a delegate for the layer builder event handler function signature. */
export type LayerBuilderDelegate = EventDelegateBase<LayerCreatorController, LayerBuilderEvent, void>;

/** Defines the event payload for the layer config error delegate. */
export interface LayerConfigErrorEvent {
  /** The layer path (or the geoview layer id) depending when the error occurs in the process. */
  layerPath: string;

  /** The error message. */
  error: string;
}

/** Defines a delegate for the layer config error event handler function signature. */
export type LayerConfigErrorDelegate = EventDelegateBase<LayerCreatorController, LayerConfigErrorEvent, void>;
